import {
  Component,
  Input,
  HostBinding,
  EventEmitter,
  Output,
  TemplateRef,
  ViewChild,
  ElementRef,
  ViewContainerRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { OverlayRef, Overlay } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

@Component({
  selector: 'suka-color-input',
  template: `
    <label *ngIf="skeleton && label" class="label skeleton"></label>
    <label *ngIf="!skeleton && label" [for]="id" class="label">
      <ng-container *ngIf="!isTemplate(label)">{{label}}</ng-container>
      <ng-template *ngIf="isTemplate(label)" [ngTemplateOutlet]="label"></ng-template>
    </label>
    <div *ngIf="!skeleton && helperText" class="form__helper-text">
      <ng-container *ngIf="!isTemplate(helperText)">{{helperText}}</ng-container>
      <ng-template *ngIf="isTemplate(helperText)" [ngTemplateOutlet]="helperText"></ng-template>
    </div>
    <div
      data-color-input
      [attr.data-invalid]="(invalid ? true : null)"
      [attr.data-valid]="(valid ? true : null)"
      class="color-input"
      [ngClass]="{
        'color-input--nolabel': !label,
        'color-input--helpertext': helperText,
        'skeleton' : skeleton,
        'color-input--disabled': disabled
      }">
      <div class="color-input__input-wrapper">
        <div
          class="color-input__color-preview"
          *ngIf="value"
          [ngStyle]="{'background-color': value || 'white'}"
          (click)="openColorDialog()"
        ></div>
        <svg
          class="color-input__color-preview-icon"
          *ngIf="!value"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          (click)="openColorDialog()"
        >
          <path d="M8 0C3.55556 0 0 3.55556 0 8C0 12.4444 3.55556 16 8 16C8.71111 16 9.33333 15.3778 9.33333 14.6667C9.33333 14.3111 9.24444 14.0444 8.97778 13.7778C8.8 13.5111 8.62222 13.2444 8.62222 12.8889C8.62222 12.1778 9.24448 11.5556 9.95556 11.5556H11.5556C14.0444 11.5556 16 9.6 16 7.11111C16 3.2 12.4444 0 8 0ZM3.11111 8C2.4 8 1.77778 7.37778 1.77778 6.66667C1.77778 5.95556 2.4 5.33333 3.11111 5.33333C3.82222 5.33333 4.44444 5.95556 4.44444 6.66667C4.44444 7.37778 3.82222 8 3.11111 8ZM5.77778 4.44444C5.06667 4.44444 4.44444 3.82222 4.44444 3.11111C4.44444 2.4 5.06667 1.77778 5.77778 1.77778C6.48889 1.77778 7.11111 2.4 7.11111 3.11111C7.11111 3.82222 6.48889 4.44444 5.77778 4.44444ZM10.2222 4.44444C9.51111 4.44444 8.88889 3.82222 8.88889 3.11111C8.88889 2.4 9.51111 1.77778 10.2222 1.77778C10.9333 1.77778 11.5556 2.4 11.5556 3.11111C11.5556 3.82222 10.9333 4.44444 10.2222 4.44444ZM12.8889 8C12.1778 8 11.5556 7.37778 11.5556 6.66667C11.5556 5.95556 12.1778 5.33333 12.8889 5.33333C13.6 5.33333 14.2222 5.95556 14.2222 6.66667C14.2222 7.37778 13.6 8 12.8889 8Z" fill="#677784"/>
        </svg>

        <input
          #origin
          type="text"
          [id]="id"
          [placeholder]="placeholder"
          [value]="value"
          [disabled]="disabled"
          [required]="required"
          (focus)="openColorDialog()"
          (change)="onColorInputChange($event)"/>

        <suka-icon *ngIf="!skeleton && invalid" icon="alert-circle" class="color-input__invalid"></suka-icon>
        <suka-icon *ngIf="!skeleton && valid" icon="check" class="color-input__valid"></suka-icon>
      </div>
      <div *ngIf="invalid" class="form-requirement">
        <ng-container *ngIf="!isTemplate(invalidText)">{{invalidText}}</ng-container>
        <ng-template *ngIf="isTemplate(invalidText)" [ngTemplateOutlet]="invalidText"></ng-template>
      </div>
    </div>

    <ng-template #colorPickerDialog>
      <div class="color-picker-dialog">
        <suka-color-picker [color]="value" (colorChange)="onColorChange($event)">
        </suka-color-picker>
      </div>
    </ng-template>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ColorInput,
      multi: true
    }
  ]
})
export class ColorInput implements ControlValueAccessor {
  /**
   * Variable used for creating unique ids for color input components.
   */
  static colorInputCount = 0;
  private _overlayRef: OverlayRef;
  private _portal: TemplatePortal<any>;

  @HostBinding('class.form-item') containerClass = true;
  /**
   * Set to `true` for a disabled color input.
   */
  @Input() disabled = false;
  /**
   * Set to `true` for a loading color input component.
   */
  @Input() skeleton = false;
  /**
   * Set to `true` for an invalid color input component.
   */
  @Input() invalid = false;
  /**
   * Set to `true` for a valid color input component.
   */
  @Input() valid = false;
  /**
   * The unique id for the color input component.
   */
  @Input() id = `color-input-${ColorInput.colorInputCount}`;
  /**
   * Reflects the required attribute of the `input` element.
   */
  @Input() required: boolean;
  /**
   * Sets the value attribute on the `input` element.
   */
  @Input() value = '';
  /**
   * Sets the placeholder on the `input` element.
   */
  @Input() placeholder = '';
  /**
   * Sets the text inside the `label` tag.
   */
  @Input() label: string | TemplateRef<any>;
  /**
   * Sets the optional helper text.
   */
  @Input() helperText: string | TemplateRef<any>;
  /**
   * Sets the invalid text.
   */
  @Input() invalidText: string | TemplateRef<any>;
  /**
   * Emits event notifying other classes when a change in state occurs in the input.
   */
  // tslint:disable-next-line: no-output-native
  @Output() change = new EventEmitter();

  @ViewChild('origin', { static: false }) origin: ElementRef;
  @ViewChild('colorPickerDialog', { static: false }) dialog: TemplateRef<any>;

  /**
   * Creates an instance of `ColorInput`.
   */
  constructor(
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef
  ) {
    ColorInput.colorInputCount++;
  }

  /**
   * This is the initial value set to the component
   * @param value The input value.
   */
  public writeValue(value: any) {
    this.value = value;
  }

  /**
   * Sets a method in order to propagate changes back to the form.
   */
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  /**
   * Registers a callback to be triggered when the control has been touched.
   * @param fn Callback to be triggered when the number input is touched.
   */
  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state through the model
   */
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  /**
   * Called when number input is blurred. Needed to properly implement `ControlValueAccessor`.
   */
  onTouched: () => any = () => { };

  /**
   * Method set in `registerOnChange` to propagate changes back to the form.
   */
  propagateChange = (_: any) => { };

  /**
   * Creates a class of `NumberChange` to emit the change in the `Number`.
   */
  emitChangeEvent(): void {
    this.change.emit(this.value);
    this.propagateChange(this.value);
  }

  onColorChange(value) {
    this.value = value;
    this.emitChangeEvent();
  }

  onColorInputChange(event) {
    this.value = event.target.value;
    this.emitChangeEvent();
  }

  /**
   * Opens the color picker dialog
   */
  openColorDialog() {
    if (!this._overlayRef) {
      const positionStrategy = this._overlay
        .position()
        .flexibleConnectedTo(this.origin)
        .withPositions([{
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 8,
        }]);

      this._overlayRef = this._overlay.create({
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        positionStrategy,
      });

      this._overlayRef.backdropClick().subscribe(() => this._overlayRef.detach());
      this._portal = new TemplatePortal(this.dialog, this._viewContainerRef);

    }

    this._overlayRef.attach(this._portal);
  }

  /**
   * Closes the color picker dialog
   */
  closeColorDialog() {
    this._overlayRef.detach();
  }

  /**
   * Checks if it is a template
   */
  public isTemplate(value) {
    return value instanceof TemplateRef;
  }
}
